import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageToggle from './LanguageToggle';
import VocationWorksLogo from './VocationWorksLogo';

const NAVY      = '#0D1B2A';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

function CtaButton({ cta, variant, fullWidth, onAfterClick, compact }) {
  if (!cta) return null;

  const base = {
    borderRadius: '6px',
    padding: compact ? '9px 14px' : '9px 20px',
    fontWeight: 700,
    fontSize: '0.875rem',
    fontFamily: FONT_BODY,
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    cursor: 'pointer',
    border: 'none',
    textAlign: 'center',
  };

  const variantStyle = variant === 'outline'
    ? { backgroundColor: 'transparent', color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.3)' }
    : variant === 'white'
      ? { backgroundColor: WHITE, color: NAVY }
      : { backgroundColor: AMBER, color: NAVY };

  const widthStyle = fullWidth ? { width: '100%', boxSizing: 'border-box' } : {};

  // In the phone menu: a 44px-tall tap target.
  const menuStyle = fullWidth ? { minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center' } : {};

  const style = { ...base, ...variantStyle, ...widthStyle, ...menuStyle };

  if (cta.href) {
    return (
      <a
        href={cta.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onAfterClick}
        style={{ ...style, display: fullWidth ? 'flex' : 'inline-block' }}
      >
        {cta.label}
      </a>
    );
  }

  return (
    <button
      onClick={() => { cta.onClick?.(); onAfterClick?.(); }}
      style={style}
    >
      {cta.label}
    </button>
  );
}

// The full nav row (logo, three audience links, language toggle, sign in and up to two buttons)
// doesn't fit every width: its width depends on the page's buttons and the language (Spanish
// labels are much longer), so it collided anywhere from ~770px up to ~1,200px. Instead of a fixed
// breakpoint, a hidden copy of the full row is measured and the hamburger menu takes over whenever
// that row wouldn't fit -- and always below 768px. In between, a compact row (tighter gaps,
// padding and buttons) is tried first, so desktop keeps the full row as long as it can.
const MOBILE_MAX = 768;
const FIT_MARGIN = 16; // keep a little air between the groups
const SPACING = {
  normal:  { rowGap: 16, rowPadding: 32, linkGap: 20, rightGap: 12 },
  compact: { rowGap: 12, rowPadding: 20, linkGap: 14, rightGap: 8 },
};

export default function SiteNavbar({ activePage, signInPath = '/login', primaryCta, secondaryCta }) {
  const { t, i18n } = useTranslation();
  // layout: 'normal' | 'compact' | 'menu'
  const [layout, setLayout] = useState(window.innerWidth < MOBILE_MAX ? 'menu' : 'normal');
  const isMobile = layout === 'menu';
  const [menuOpen, setMenuOpen] = useState(false);
  const measureRef = useRef(null);
  const measureCompactRef = useRef(null);

  const NAV_LINKS = [
    { key: 'trades',    label: t('siteNavbar.forWorkers'),   path: '/trades' },
    { key: 'employers', label: t('siteNavbar.forEmployers'), path: '/employers' },
    { key: 'union',     label: t('siteNavbar.forUnions'),    path: '/union' },
  ];

  useLayoutEffect(() => {
    const needed = (el, sp) => {
      const groups = el ? [...el.children].map(c => c.getBoundingClientRect().width) : [];
      return groups.reduce((a, b) => a + b, 0) + sp.rowGap * 2 + sp.rowPadding * 2 + FIT_MARGIN;
    };
    const update = () => {
      const w = window.innerWidth;
      setLayout(w < MOBILE_MAX ? 'menu'
        : w >= needed(measureRef.current, SPACING.normal) ? 'normal'
        : w >= needed(measureCompactRef.current, SPACING.compact) ? 'compact'
        : 'menu');
    };
    update();
    window.addEventListener('resize', update);
    document.fonts?.ready.then(update);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (ro) [measureRef.current, measureCompactRef.current].forEach(el => el && [...el.children].forEach(c => ro.observe(c)));
    return () => { window.removeEventListener('resize', update); ro?.disconnect(); };
  }, [i18n.language, primaryCta?.label, secondaryCta?.label]);

  useEffect(() => {
    if (!isMobile) setMenuOpen(false);
  }, [isMobile]);

  const logo = (
    <Link
      to="/"
      style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', textDecoration: 'none' }}
    >
      <VocationWorksLogo variant="reversed" size={36} />
    </Link>
  );
  const renderLinks = sp => (
    <div style={{
      minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: `${sp.linkGap}px`,
    }}>
      {NAV_LINKS.map(link => {
        const isActive = activePage === link.key;
        return (
          <Link
            key={link.key}
            to={link.path}
            aria-current={isActive ? 'page' : undefined}
            style={{
              color: isActive ? AMBER : WHITE,
              fontWeight: isActive ? 700 : 500,
              fontSize: '14px', fontFamily: FONT_BODY, cursor: 'pointer',
              whiteSpace: 'nowrap', textDecoration: 'none',
            }}
          >
            {link.label}
          </Link>
        );
      })}
    </div>
  );
  const renderRight = (sp, compact) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: `${sp.rightGap}px`, justifySelf: 'end', flexShrink: 0 }}>
      <LanguageToggle />
      <Link
        to={signInPath}
        style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap', textDecoration: 'none' }}
      >
        {t('siteNavbar.signIn')}
      </Link>
      <CtaButton cta={secondaryCta} variant={secondaryCta?.variant} compact={compact} />
      <CtaButton cta={primaryCta} variant="primary" compact={compact} />
    </div>
  );
  const sp = layout === 'compact' ? SPACING.compact : SPACING.normal;
  const measureStyle = {
    position: 'absolute', top: 0, left: 0, height: 0, overflow: 'hidden', visibility: 'hidden',
    display: 'flex', alignItems: 'center', width: 'max-content', pointerEvents: 'none',
  };

  return (
    <nav style={{
      position: 'sticky', top: 0, left: 0, right: 0, zIndex: 1000,
      height: '64px',
      display: isMobile ? 'flex' : 'grid',
      gridTemplateColumns: isMobile ? undefined : 'auto 1fr auto',
      alignItems: 'center', justifyContent: isMobile ? 'space-between' : undefined,
      gap: `${sp.rowGap}px`, padding: isMobile ? '0 2rem' : `0 ${sp.rowPadding}px`,
      backgroundColor: 'rgba(13,27,42,0.95)',
      backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
      borderBottom: '1px solid rgba(245,166,35,0.2)',
    }}>
      {/* Hidden copies of the full desktop row (normal and compact), only for measuring (see above). */}
      <div ref={measureRef} data-nav-measure aria-hidden="true" style={measureStyle}>
        {logo}{renderLinks(SPACING.normal)}{renderRight(SPACING.normal, false)}
      </div>
      <div ref={measureCompactRef} data-nav-measure aria-hidden="true" style={measureStyle}>
        {logo}{renderLinks(SPACING.compact)}{renderRight(SPACING.compact, true)}
      </div>

      {logo}

      {!isMobile && (
        <>
          {renderLinks(sp)}
          {renderRight(sp, layout === 'compact')}
        </>
      )}

      {isMobile && (
        <button
          type="button"
          onClick={() => setMenuOpen(o => !o)}
          aria-expanded={menuOpen}
          aria-label={t('siteNavbar.toggleMenu')}
          style={{
            cursor: 'pointer', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'none', border: 'none', padding: 0, margin: '0 -10px 0 0', font: 'inherit',
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      )}

      {isMobile && menuOpen && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
          backgroundColor: NAVY, padding: '20px', borderBottom: `1px solid ${AMBER}`,
          display: 'flex', flexDirection: 'column', gap: '6px',
        }}>
          {NAV_LINKS.map(link => {
            const isActive = activePage === link.key;
            return (
              <Link
                key={link.key}
                to={link.path}
                onClick={() => setMenuOpen(false)}
                aria-current={isActive ? 'page' : undefined}
                style={{
                  color: isActive ? AMBER : WHITE,
                  fontWeight: isActive ? 700 : 500,
                  fontSize: '15px', fontFamily: FONT_BODY, cursor: 'pointer', textDecoration: 'none',
                  minHeight: '44px', display: 'flex', alignItems: 'center',
                }}
              >
                {link.label}
              </Link>
            );
          })}
          <Link
            to={signInPath}
            onClick={() => setMenuOpen(false)}
            style={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer', textDecoration: 'none', minHeight: '44px', display: 'flex', alignItems: 'center' }}
          >
            {t('siteNavbar.signIn')}
          </Link>
          <CtaButton cta={secondaryCta} variant={secondaryCta?.variant} fullWidth onAfterClick={() => setMenuOpen(false)} />
          <CtaButton cta={primaryCta} variant="primary" fullWidth onAfterClick={() => setMenuOpen(false)} />
          <LanguageToggle large fullWidth />
        </div>
      )}
    </nav>
  );
}
